import { addSlashes } from '@farmnet/helper';

/**
 * @ngdoc service
 * @name farmnet.base.tracking.gtm
 * @requires $window
 *
 * @description
 * Google Tag Manager service.
 */
GTMService.$inject = ['$window'];

function GTMService($window) {
  function warnDeprecation() {
    console.warn(`[GTMService] This service is **deprecated** (09.01.2019) and will be removed on: 24.03.2019. Please start using '@farmnet/tag-manager' today!`);
  };

  /**
   * Share the current user navigation location state between `GTMService` consumers.
   *
   * @property {function} get - Get location.
   * @property {function} set - Set location.
   */
  let _location = undefined;
  this.userLocation = {
    /**
     * Get the current user location.
     *
     * @returns {string} - The location.
     */
    get: () => _location,
    /**
     * Set the current user location.
     *
     * @param {string} - Current navigation location.
     */
    set: (currentLocation) => {
      _location = currentLocation;
    }
  };

  /**
   * Sets hashed userId to be used within tracking.
   *
   * @param {string} userIdHash
   */
  this.setUserId = function(userIdHash) {
    var event = {
      event: 'Page Meta',
      uiUserId: userIdHash
    };

    $window.dataLayer.push(event);
    warnDeprecation();
  };

  /**
   * Sets the metadata for all Google Tag Manager `dataLayer.push`es.
   *
   * @param {object} metadata - Google Tag Manager metadata.
   *
   * @example
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'language': 'de', // on all sites
      'country': 'de', // on all sites
      'internalTraffic': 'false', // on all sites
      'uiLoggedIn': 'true', // only during log in
      'environment': 'testing', // on all sites
      'pageType': 'home', // on all sites
      'breakPoint': 'mid', // on all sites
      'uiUserId': '1039967', // on every page the user is logged in
      'uiFarmSize': '110', // only during login
      'uiFarmType': 'crop', // only during login
      'uiCompanyId': '10098', // only during login
      'uiPostalCode': '22303', // only during login
      'uiNewsletterSubscriber': 'true', // only during login and account registration
      'uiCustomerType': 'active', // only during login
      'uiEmployees': '4' // only during login
    });
   */
  this.setMetadata = function(metadata) {
    $window.dataLayer.push(metadata);
    warnDeprecation();
  };

  /**
   * Logs general event with an event category (Videos, Music, Games, ...), an event action
   * (Play, Pause, Duration, Add Playlist, Downloaded, Clicked, ...), and an optional
   * event label and optional numeric value.
   *
   * @param {string} category
   * @param {string} action
   * @param {string} [label]
   * @param {number} [value]
   *
   * @example
    window.dataLayer = window.dataLayer || [];
    dataLayer.push({
      'event' : 'uaevent',
      'eventCategory': 'Navigation',
      'eventAction': 'Top_L3',
      'eventLabel': 'Product::Components::Crop Management'
    });
   */
  this.trackEvent = function(category, action, label, value) {
    var event = {
      event: 'uaevent',
      eventCategory: category,
      eventAction: action
    };

    if (label !== undefined) {
      event.eventLabel = label;
    }

    if (value !== undefined) {
      event.eventValue = value;
    }

    $window.dataLayer.push(event);
    warnDeprecation();
  };

  /**
   * Track virtual pagepaths.
   *
   * @param {string} title
   * @param {string} virtPath
   *
   * @example
    window.dataLayer = window.dataLayer || [];
    dataLayer.push({
      'event': 'virtPath',
      'pageTitle': 'Components',
      'pagePath': '/components/my-components'
    });
   */
  this.trackVirtualPath = function(title, virtPath) {
    var event = {
      event: 'virtPath',
      pageTitle: title,
      pagePath: addSlashes(virtPath)
    };

    $window.dataLayer.push(event);
    warnDeprecation();
  };

  /**
   * Track promotion impression.
   *
   * @param {array} promotions
   *
   * @example
    window.dataLayer = window.dataLayer || [];
    dataLayer.push({
      'event': 'EECpromotionImpression',
      'ecommerce': {
        'promoView': {
          'promotions': [
          {
            'name': 'DLG Feldtage',
            'creative': 'skyscraper_v1',
            'position': 1
          },
          // add further promotion impressions
          ]
        }
      }
    });
   */
  this.trackPromotionImpression = function(promotions) {
    var event = {
      event: 'EECpromotionImpression',
      ecommerce: {
        promoView: {
          promotions: promotions
        }
      }
    };

    $window.dataLayer.push(event);
    warnDeprecation();
  };

  /**
   * Track promotion click. Callback is optional.
   *
   * @param {array} promotions
   * @param {function} [callback]
   *
   * @example
    window.dataLayer = window.dataLayer || [];
    dataLayer.push({
      'event': 'EECpromotionClick',
      'ecommerce': {
        'promoClick': {
          'promotions': [{
            'name': 'DLG Feldtage',
            'creative': 'skyscraper_v1',
            'position': 1
          }]
        }
      }
    });
   */
  this.trackPromotionClick = function(promotions, callback) {
    var event = {
      event: 'EECpromotionClick',
      ecommerce: {
        promoClick: {
          promotions: promotions
        }
      },
      eventCallback: callback
    };

    $window.dataLayer.push(event);
    warnDeprecation();
  };

  /**
   * Track product list impression.
   *
   * @param {string} currency
   * @param {array} impressions
   *
   * @example
    window.dataLayer = window.dataLayer || [];
    dataLayer.push({
      'event': 'EECproductImpression',
      'ecommerce': {
        'currencyCode': 'EUR',
        'impressions': [{
          'name': 'Stock basic',
          'price': '0.60', // price without tax
          'brand': '365FarmNet',
          'category': 'Plant',
          'list': 'Crop management',
          'position': 1
          },
          // add further products
        ]
      }
    });
   */
  this.trackProductImpression = function(currency, impressions) {
    var event = {
      event: 'EECproductImpression',
      ecommerce: {
        currencyCode: currency,
        impressions: impressions
      }
    };

    $window.dataLayer.push(event);
    warnDeprecation();
  };

  /**
   * Track product click in product list.
   *
   * @param {string} currency
   * @param {string} list
   * @param {array} product
   *
   * @example
    window.dataLayer = window.dataLayer || [];
    dataLayer.push({
      'event': 'EECproductClick',
      'ecommerce': {
        'currencyCode': 'EUR',
        'click': {
          'actionField': {'list': 'Name of product list e.g. breadcrumbs'},
          'products': [{
            'name': 'Stock basic',
            'price': '0.60', // price without tax
            'brand': '365FarmNet',
            'category': 'Plant',
            'position': 1
          }]
        }
      }
    });
   */
  this.trackProductClick = function(currency, list, products) {
    var event = {
      event: 'EECproductClick',
      ecommerce: {
        currencyCode: currency,
        click: {
          actionField: { list: list },
          products: products
        }
      }
    };

    $window.dataLayer.push(event);
    warnDeprecation();
  };

  /**
   * Track product detail page view.
   *
   * @param {string} list
   * @param {array} products
   *
   * @example
    window.dataLayer = window.dataLayer || [];
    dataLayer.push({
      'event': 'EECproductDetailView',
      'ecommerce': {
        'currencyCode': 'EUR',
        'detail': {
          'products': [{
            'name': 'AMAZONE fertiliser service',
            'id': '', // Component-id if available
            'price': '0.50', // price without tax
            'brand': 'AMAZONE',
            'category': 'Machine settings'
          }]
        }
      }
    });
   */
  this.trackProductDetailView = function(currency, products) {
    var event = {
      event: 'EECproductDetailView',
      ecommerce: {
        currencyCode: currency,
        detail: {
          products: products
        }
      }
    };

    $window.dataLayer.push(event);
    warnDeprecation();
  };

  /**
   * Track product booking.
   *
   * @param {object} action
   * @param {array} products
   *
   * @example
   window.dataLayer = window.dataLayer || [];
   dataLayer.push({
     'event': 'EECaddToCart',
     'ecommerce': {
        'currencyCode': 'EUR',
        'add': {
          'products': [{
            'name': 'AMAZONE fertiliser service',
            'id': '', // Component-id if available
            'price': '0.50', // Component price without tax
            'brand': 'AMAZONE',
            'category': 'Machine settings',
            'quantity': 1
          }]
        }
      }
    });
  */
  // TODO: [] Do we really need product booking?
  this.trackAddToCart = function(currency, products) {
    var event = {
      event: 'EECaddToCart',
      ecommerce: {
        currencyCode: currency,
        add: {
          products: products
        }
      }
    };

    $window.dataLayer.push(event);
    warnDeprecation();
  };

  /**
   * Track product purchase.
   *
   * @param {object} action
   * @param {array} products
   *
   * @example
    window.dataLayer = window.dataLayer || [];
    dataLayer.push({
      'event': 'EECpurchase',
      'ecommerce': {
        'currencyCode': 'EUR',
        'purchase': {
          'actionField': {
            'id': '', // Transaction id
            'revenue': '0.50', // Total transaction value (includes fees for payment services - without tax)
            'tax': '', // Tax for revenues if available
            'coupon': 'H4RVEST' // Transaction Promotion Code
          },
          'products': [{
            'name': 'AMAZONE fertiliser service',
            'price': '0.50', // Price without tax / single price
            'brand': 'AMAZONE',
            'category': 'Machine settings',
            'quantity': 1,
            'coupon': 'H4RVEST' // Product Promotion Code
          }]
        }
      },
    });
   */
  this.trackPurchase = function(currency, action, products) {
    var event = {
      event: 'EECpurchase',
      ecommerce: {
        currencyCode: currency,
        purchase: {
          actionField: action,
          products: products
        }
      },
    };

    $window.dataLayer.push(event);
    warnDeprecation();
  };

  /**
   * Track product checkout.
   *
   * @param {string} currency
   * @param {number} step
   * @param {string} option
   * @param {array} products
   *
   * @example
    window.dataLayer = window.dataLayer || [];
    dataLayer.push({
      'event': 'EECcheckout',
      'ecommerce': {
        'currencyCode': '{currency}',
        'checkout': {
          'actionField': {'step': 2, 'option': 'Advance Payment'},
          'products': [{
            'name': '{Component Name}',
            'price': '{Component Price without tax - e.g. 0.50}',
            'brand': '{Brand}',
            'category': '{Component Category}',
            'quantity': '{Component Quantity}'
          }]
        }
      }
    });
   */
  // TODO: [] Make option configurable
  this.trackCheckout = function(currency, step, option, products) {
    var event = {
      event: 'EECcheckout',
      ecommerce: {
        currencyCode: currency,
        checkout: {
          actionField: { step: step, option: option },
          products: products
        }
      }
    };

    $window.dataLayer.push(event);
    warnDeprecation();
  };

  /**
   * Track product checkout option.
   *
   * @param {number} step
   * @param {string} checkoutOption
   *
   * @example
    <script>
    function onCheckoutOption(step, checkoutOption) {
      dataLayer.push({
        'event': 'EECcheckoutOption',
        'ecommerce': {
          'checkout_option': {
            'actionField': {'step': step, 'option': checkoutOption}
          }
        }
      });
    }
    onCheckoutOption(2, 'credit cart')
    </script>
   */
  this.trackCheckoutOption = function(step, checkoutOption) {
    var event = {
      event: 'EECcheckoutOption',
      ecommerce: {
        checkout_option: {
          actionField: {step: step, option: checkoutOption}
        }
      }
    };

    $window.dataLayer.push(event);
    warnDeprecation();
  };
}

export default GTMService;
